import { useEffect, useRef, useState } from "react"

type UseScrollDirectionOpitions = {
    mobileTopThresholdSelector?: string
    mobileThresholdFactor?: number
    mobileMaxWidth?: number
}

export const useScrollDirection = (options?: UseScrollDirectionOpitions) => {
    const [atTop, setAtTop] = useState(true)
    const [scrollDirection, setScrollDirection] = useState<string | null>(null)
    const lastScrollY = useRef(0)
    const frameRequested = useRef(false)
    const topThresholdRef = useRef(20)
    const {
        mobileTopThresholdSelector,
        mobileThresholdFactor = 0.5,
        mobileMaxWidth = 768
    } = options || {}

    useEffect(() => {
        if (typeof window === "undefined") return
        const computeMobileThreshold = () => {
            if (window.innerWidth >= mobileMaxWidth) {
                topThresholdRef.current = 20
                return
            }

            if (mobileTopThresholdSelector) {
                const element = document.querySelector(
                    mobileTopThresholdSelector
                ) as HTMLElement | null
                if (element) {
                    const elementHeight =
                        element.offsetHeight || element.getBoundingClientRect().height
                    topThresholdRef.current = Math.max(
                        0,
                        elementHeight * mobileThresholdFactor
                    )
                    return
                }
            }
            topThresholdRef.current = 20
        }

        lastScrollY.current = window.pageYOffset
        computeMobileThreshold()

        const updateScrollDirection = () => {
            const scrollY = window.pageYOffset
            const direction = scrollY > lastScrollY.current ? "down" : "up"
            const isAtTop = scrollY < topThresholdRef.current

            setAtTop(prev => (prev !== isAtTop ? isAtTop : prev))
            if (Math.abs(scrollY - lastScrollY.current) > 1) {
                setScrollDirection(prev => (prev !== direction ? direction : prev))
            }
            lastScrollY.current = scrollY
            frameRequested.current = false
        }

        const requestTick = () => {
            if (!frameRequested.current) {
                requestAnimationFrame(updateScrollDirection)
                frameRequested.current = true
            }
        }

        window.addEventListener("scroll", requestTick, { passive: true })
        updateScrollDirection()

        return () => window.removeEventListener("scroll", requestTick)
    }, [mobileTopThresholdSelector, mobileThresholdFactor, mobileMaxWidth])

    return { scrollDirection, atTop }
}
